import { useState } from 'react';
import { Search, X } from 'lucide-react';
import type { FollowUpSelectorProps } from './types';

export function FollowUpSelector({ isOpen, onClose, onSelect, availableQuestions, currentFollowUps, parentQuestion }: FollowUpSelectorProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedQuestions, setSelectedQuestions] = useState<string[]>([]);

  const filteredQuestions = availableQuestions.filter(q =>
    q.id !== parentQuestion.id &&
    !currentFollowUps.includes(q.id) &&
    (q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.answer.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleToggleQuestion = (questionId: string) => {
    setSelectedQuestions(prev =>
      prev.includes(questionId) ? prev.filter(id => id !== questionId) : [...prev, questionId]
    );
  };

  const handleConfirm = () => {
    onSelect(selectedQuestions);
    setSelectedQuestions([]);
    setSearchTerm('');
    onClose();
  };

  const handleCancel = () => {
    setSelectedQuestions([]);
    setSearchTerm('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white  p-6 w-full max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">
            Add Follow-up Questions to: "{parentQuestion.question}"
          </h3>
          <button onClick={handleCancel} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-4">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300  focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto mb-4">
          {filteredQuestions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {searchTerm ? 'No questions match your search.' : 'No available questions to add as follow-ups.'}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredQuestions.map(question => (
                <div
                  key={question.id}
                  className={`p-3 border  cursor-pointer transition-colors ${selectedQuestions.includes(question.id)
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 hover:border-gray-300'
                    }`}
                  onClick={() => handleToggleQuestion(question.id)}
                >
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      checked={selectedQuestions.includes(question.id)}
                      className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 "
                    />
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{question.question}</h4>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{question.answer}</p>
                      <div className="flex items-center space-x-2 mt-2">
                        {question.is_root && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Root Question
                          </span>
                        )}
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${question.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                          {question.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-between items-center pt-4 border-t">
          <p className="text-sm text-gray-600">
            {selectedQuestions.length} question{selectedQuestions.length !== 1 ? 's' : ''} selected
          </p>
          <div className="flex space-x-3">
            <button type="button" onClick={handleCancel} className="px-4 py-2 bg-gray-200 text-gray-800  hover:bg-gray-300">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={selectedQuestions.length === 0}
              className="px-4 py-2 bg-indigo-600 text-white  hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add {selectedQuestions.length} Follow-up{selectedQuestions.length !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
